'use client';

import {
  arquivarDocumentoPadrao,
  atualizarDocumentoPadrao,
  criarDocumentoPadrao,
  duplicarDocumentoPadrao,
  excluirDocumentoPadrao,
  restaurarDocumentoPadrao,
  restaurarVersaoDocumentoPadrao,
  substituirArquivoDocumentoPadrao,
} from '@/features/documentosPadrao/store';
/**
 * Import de `services` dentro de um hook `'use client'` é a EXCEÇÃO ESCRITA da regra #1, e vale só
 * para efeito de browser cujo resultado é binário/imperativo — aqui `Blob` + `window.open` +
 * download. Um thunk poria `Blob` no Redux e acionaria o `serializableCheck`. Precedentes vivos:
 * `useBaixarDocumento`, `useConteudoDocumento`. Todo o resto desta feature vai por `dispatch`.
 */
import { documentosPadraoService } from '@/features/documentosPadrao/store/services';
import type {
  DocumentoPadrao,
  DocumentoPadraoFormInput,
  ErroMutacaoDocumentoPadrao,
} from '@/features/documentosPadrao/types/documentosPadraoTypes';
import { useToast } from '@/shared/providers';
import { useAppDispatch } from '@/store/hooks';

/** `.unwrap()` lança o `rejectValue` do thunk, não um `Error` — `instanceof Error` o descartava. */
function ehErroMutacao(erro: unknown): erro is ErroMutacaoDocumentoPadrao {
  return typeof erro === 'object' && erro !== null && 'mensagem' in erro && 'status' in erro;
}

function mensagemDoErro(erro: unknown): string | undefined {
  if (ehErroMutacao(erro)) return erro.mensagem;
  return erro instanceof Error ? erro.message : undefined;
}

export function useDocumentosPadraoAcoes() {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const executar = async <Resultado>(
    acao: () => Promise<Resultado>,
    sucesso: { titulo: string; mensagem?: string },
    erroTitulo: string,
  ): Promise<Resultado | null> => {
    try {
      const resultado = await acao();
      toast.showSuccess(sucesso.titulo, sucesso.mensagem);
      return resultado;
    } catch (erro) {
      toast.showError(erroTitulo, mensagemDoErro(erro));
      return null;
    }
  };

  const criar = (input: DocumentoPadraoFormInput) =>
    executar(
      () => dispatch(criarDocumentoPadrao(input)).unwrap(),
      {
        titulo: 'Documento salvo na biblioteca',
        mensagem: `"${input.nome}" já pode ser reutilizado nos produtos.`,
      },
      'Erro ao salvar documento',
    );

  const atualizar = (id: string, input: DocumentoPadraoFormInput, revisao: number) =>
    executar(
      () => dispatch(atualizarDocumentoPadrao({ id, input, revisao })).unwrap(),
      { titulo: 'Alterações salvas', mensagem: 'Valem para os próximos usos do documento.' },
      'Erro ao salvar alterações',
    );

  const substituirArquivo = (id: string, arquivo: File) =>
    executar(
      () => dispatch(substituirArquivoDocumentoPadrao({ id, arquivo })).unwrap(),
      {
        titulo: 'Arquivo substituído',
        mensagem: 'A versão anterior continua no histórico do documento.',
      },
      'Erro ao substituir arquivo',
    );

  const restaurarVersao = (id: string, versao: number) =>
    executar(
      () => dispatch(restaurarVersaoDocumentoPadrao({ id, versao })).unwrap(),
      { titulo: `Versão v${versao} restaurada`, mensagem: 'Ela passa a ser a versão em uso.' },
      'Erro ao restaurar versão',
    );

  const duplicar = (documento: DocumentoPadrao) =>
    executar(
      () => dispatch(duplicarDocumentoPadrao(documento.id)).unwrap(),
      { titulo: 'Documento duplicado', mensagem: `Criamos uma cópia de "${documento.nome}".` },
      'Erro ao duplicar documento',
    );

  const arquivar = (documento: DocumentoPadrao) =>
    executar(
      () => dispatch(arquivarDocumentoPadrao(documento.id)).unwrap(),
      {
        titulo: 'Documento arquivado',
        mensagem: 'Ele sai das sugestões e não pode mais ser anexado.',
      },
      'Erro ao arquivar documento',
    );

  const restaurar = (documento: DocumentoPadrao) =>
    executar(
      () => dispatch(restaurarDocumentoPadrao(documento.id)).unwrap(),
      { titulo: 'Documento restaurado', mensagem: 'Ele volta a aparecer como sugestão.' },
      'Erro ao restaurar documento',
    );

  /**
   * `true` quando o documento não existe mais: excluído agora, ou antes por outra pessoa (404) — aí
   * não há o que confirmar e o diálogo fecha. No 409 da trava ele fica aberto e vira o bloqueado.
   */
  const excluir = async (documento: DocumentoPadrao): Promise<boolean> => {
    try {
      await dispatch(excluirDocumentoPadrao(documento.id)).unwrap();
      toast.showSuccess('Documento excluído', `"${documento.nome}" saiu da biblioteca.`);
      return true;
    } catch (erro) {
      toast.showError('Erro ao excluir documento', mensagemDoErro(erro));
      return ehErroMutacao(erro) && erro.status === 404;
    }
  };

  // Pré-visualizar e baixar passam pelo proxy de bytes da API (`fetch`, nunca navegação): o header
  // de workspace é injetado dentro do request, e um `<a href>` não o carrega.
  const preVisualizar = (documento: DocumentoPadrao, versao?: number) =>
    executar(
      () => documentosPadraoService.preVisualizar(documento.id, versao),
      { titulo: 'Abrindo pré-visualização' },
      'Erro ao pré-visualizar arquivo',
    );

  const baixar = (documento: DocumentoPadrao, versao?: number) =>
    executar(
      () => documentosPadraoService.baixar(documento.id, documento.arquivo.nomeArquivo, versao),
      { titulo: 'Download iniciado' },
      'Erro ao baixar arquivo',
    );

  return {
    arquivar,
    atualizar,
    baixar,
    criar,
    duplicar,
    excluir,
    restaurar,
    preVisualizar,
    restaurarVersao,
    substituirArquivo,
  };
}
